import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { AuthenticationService } from '../services/authentication.service';
import { ResourceService } from '../services/resource.service';
import { Resource } from '../shared/models/resource';
import { ToastService } from '../shared/toast/toast.service';
import { DeleteActionComponent } from './delete-action.component';

@Component({
  selector: 'pps-manage-resource',
  templateUrl: './manage-resource.component.html',
  styles: [
  ],
})
export class ManageResourceComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  items: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Links", icon: "pi pi-fw pi-link"},
  ];
  
  resources: Resource[] = [];
  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => `${param.data.id}`,
    defaultColDef:{
      enableCellChangeFlash: true
    }
  };

  private gridApi;

  constructor(private _resourceService: ResourceService,
              private _confirm: ConfirmationService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private breakpointObserver: BreakpointObserver) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  addNewLink(){
    let maxOrder:number = 0;
    const resourceOrders = this.resources.map(r=>r.orderNo);
    if (resourceOrders.length > 0){
       maxOrder = Math.max(...resourceOrders);
    }
    
    let newLink: Resource = {
      name: "New link",
      url: "https://",
      active:true,
      orderNo: maxOrder + 1
    };

    this._spinner.show(this.spinnerName);

    this.subs.sink = this._resourceService.add(newLink)
      .subscribe(res => {
        if (res.succeed){
          this.resources.push(res.result);
          this.gridApi.setGridOption('rowData', this.resources);
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.resources.length = 0;
    this.subs.sink = this._resourceService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.resources = apiResponse.result;
          this._toast.succeeded(`${this.resources.length} links loaded`);
        }else{
          this._toast.failed(`Failed to load links: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }
  delete(resource: Resource){
    if (resource === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Link`,
      message: `Are you sure that you want to delete [${resource.name}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(resource)
    });
  }


  deleteHelper(resource: Resource){
    this.subs.sink = this._resourceService.delete(resource.id)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.resources.findIndex(x => x.id == resource.id);
          if (foundIndex !== -1 ){
            this.resources.splice(foundIndex, 1);
            this.gridApi.setGridOption('rowData', this.resources);
            this._toast.succeeded(`[${resource.name}] deleted!`);
          }else{
            this._toast.failed(`Delete [${resource.name}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${resource.name}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }

  private getActionComponentTooltip(resource: Resource):string{
    return "Click to delete link: " + resource.name;
  }  

  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Order No',
        field:'orderNo',
        width: 60,
        editable: true,
        onCellValueChanged: this.resourceChanged,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Name',
        field:'name',
        filter: false,
        width: 300,
        editable: true,
        onCellValueChanged: this.resourceChanged
      },
      {
        headerName: 'Link Address',
        field:'url',
        filter: false,
        flex: 1,
        editable: true,
        onCellValueChanged: this.resourceChanged
      }
    ];

    if (this.smallScreen){
      cols[cols.length-1].flex = 0;
      cols[cols.length-1].width = 500;
    }

    return cols;
  }

  private resourceChanged(params){
    let _this = params.context.componentParent;
    let resource = params.data;
    let shouldReload = params.colDef.field === "orderNo" && +params.newValue !== +params.oldValue;

    if (params.colDef.field === "orderNo" && isNaN(resource.orderNo)){
      resource.orderNo = 0;
    }

    _this._spinner.show(_this.spinnerName);
    resource.orderNo = +resource.orderNo;
    _this.subs.sink = _this._resourceService.update(resource)
      .subscribe(res => {
        if (res.succeed){
          _this._toast.succeeded(`Link updated`);
          if (shouldReload){
            _this.loadData();
          }
        }else{
          _this._toast.failed(`Link update failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => _this._spinner.hide(_this.spinnerName));
  }
}
